import { appraisalDraftSchema } from './schema';
import type { AppraisalDraft } from './schema';

/**
 * Persists the appraisal wizard draft in the browser (localStorage) so an
 * in-progress request survives a page refresh. Stored data is discarded when
 * it is stale (> 24h) or no longer matches the current draft shape / storage
 * version (e.g. after a new app version changes the fields).
 */

const STORAGE_KEY = 'appraisal:draft';

/** Bump when the persisted shape changes in a backwards-incompatible way. */
const STORAGE_VERSION = 1;

const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h

type StoredDraft = {
  version: number;
  savedAt: number;
  draft: AppraisalDraft;
};

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

/**
 * Loads a persisted draft, or returns `null` when there is none, when it is
 * invalid/stale, or when running on the server. Invalid data is cleared.
 */
export function loadDraft(): AppraisalDraft | null {
  if (!isBrowser()) return null;

  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearDraft();
    return null;
  }

  const envelope = parsed as Partial<StoredDraft> | null;

  // Wrong version or shape → discard (e.g. new app version).
  if (!envelope || envelope.version !== STORAGE_VERSION || typeof envelope.savedAt !== 'number') {
    clearDraft();
    return null;
  }

  // Older than 24h → discard.
  if (Date.now() - envelope.savedAt > MAX_AGE_MS) {
    clearDraft();
    return null;
  }

  // Shape no longer matches the current schema → discard.
  const result = appraisalDraftSchema.safeParse(envelope.draft);
  if (!result.success) {
    clearDraft();
    return null;
  }

  return result.data;
}

/** Persists the current draft with a fresh timestamp. No-op on the server. */
export function saveDraft(draft: AppraisalDraft): void {
  if (!isBrowser()) return;
  const envelope: StoredDraft = { version: STORAGE_VERSION, savedAt: Date.now(), draft };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    // Ignore quota / privacy-mode errors — persistence is best-effort.
  }
}

/** Removes any persisted draft (e.g. after a successful submission). */
export function clearDraft(): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore.
  }
}
